import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  deleteField,
  orderBy,
  limit,
  startAfter,
  writeBatch,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '@/lib/firebase/firestore';
import { AdminUser, UserStats, WordUsage, DashboardStats } from '../types/admin';

// Log database info for debugging

// Get Firebase Functions instance for potential future use
// const functions = getFunctions();

// Check if user is an admin
export async function checkAdminStatus(uid: string): Promise<boolean> {
  try {
    // First check if the user is the main admin
    if (uid === 'tOCaPaZ9XDb3eBXgoNYzGPWeyY2') {
      return true;
    }

    // Check if user has isAdmin flag in users collection
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists() && userDoc.data()?.isAdmin === true) {
      return true;
    }

    // Fallback: check if user exists in admin_users collection
    const adminRef = doc(db, 'admin_users', uid);
    const adminDoc = await getDoc(adminRef);

    return adminDoc.exists();
  } catch (error) {
    return false;
  }
}

// Get admin user details
export async function getAdminUser(uid: string): Promise<AdminUser | null> {
  try {
    // First check if the user is the main admin
    if (uid === 'tOCaPaZ9XDb3eBXgoNYzGPWeyY2') {
      // Get user data from users collection
      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          uid,
          email: userData.email || '',
          displayName: userData.displayName || '',
          role: 'superadmin',
          createdAt: userData.createdAt,
          lastLogin: userData.lastLogin,
        } as AdminUser;
      }
    }

    // Check if user has isAdmin flag in users collection
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists() && userDoc.data()?.isAdmin) {
      const userData = userDoc.data();
      return {
        uid,
        email: userData.email || '',
        displayName: userData.displayName || '',
        role: 'admin',
        createdAt: userData.createdAt,
        lastLogin: userData.lastLogin,
      } as AdminUser;
    }

    // Fallback: check if user exists in admin_users collection
    const adminRef = doc(db, 'admin_users', uid);
    const adminDoc = await getDoc(adminRef);

    if (adminDoc.exists()) {
      // Get user data from users collection
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const adminData = adminDoc.data();
        return {
          uid,
          email: userData.email || adminData.email || '',
          displayName: userData.displayName || adminData.displayName || '',
          role: adminData.role || 'admin',
          createdAt: userData.createdAt || adminData.createdAt,
          lastLogin: userData.lastLogin,
        } as AdminUser;
      }
    }

    return null;
  } catch (error) {
    return null;
  }
}

// Make a user an admin
export async function makeUserAdmin(
  uid: string,
  _email: string, // Prefixed with underscore to indicate it's not used
  _displayName: string // Prefixed with underscore to indicate it's not used
): Promise<boolean> {
  try {
    // Update isAdmin flag in users collection
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      isAdmin: true,
    });

    return true;
  } catch (error) {
    return false;
  }
}

// Remove admin privileges from a user
export async function removeUserAdmin(uid: string): Promise<boolean> {
  try {
    // Update isAdmin flag in users collection
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      isAdmin: false,
    });

    return true;
  } catch (error) {
    return false;
  }
}

// Update a specific user field
export async function updateUserField(uid: string, field: string, value: any): Promise<boolean> {
  try {
    // Check if user is authenticated
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error('You must be logged in to perform this action');
    }

    // Check if the user is an admin by querying Firestore directly
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      const userData = userDoc.data();
      const isAdmin =
        userData.isAdmin === true || currentUser.uid === 'tOCaPaZ9XDb3eBXgoNYzGPWeyY2';

      if (!isAdmin) {
        throw new Error('You must be an admin to perform this action');
      }
    } catch (error) {
      throw new Error('Error checking admin status');
    }

    // Use direct Firestore access for admin operations
    // This is more reliable than using Cloud Functions when there are auth issues
    const userRef = doc(db, 'users', uid);

    // Handle nested fields like gameState.skipCount
    if (field.includes('.')) {
      const [parent, child] = field.split('.');

      // Update nested field
      await updateDoc(userRef, {
        [`${parent}.${child}`]: value,
      });
    } else {
      // Handle direct fields
      await updateDoc(userRef, {
        [field]: value,
      });
    }

    return true;
  } catch (error) {
    // Log more detailed error information
    if (error.code) {
    }

    if (error.message) {
    }

    if (error.details) {
    }

    if (error.stack) {
    }

    return false;
  }
}

// Get all users with pagination
export async function getUsers(lastUser = null, pageSize = 20) {
  try {
    let usersQuery: any;

    if (lastUser) {
      usersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        startAfter(lastUser),
        limit(pageSize)
      );
    } else {
      usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(pageSize));
    }

    const usersSnapshot = await getDocs(usersQuery);
    const users = usersSnapshot.docs.map((doc) => ({
      uid: doc.id,
      ...(doc.data() as any),
    }));

    return {
      users,
      lastVisible: usersSnapshot.docs[usersSnapshot.docs.length - 1],
    };
  } catch (error) {
    // Return empty array instead of throwing
    return {
      users: [],
      lastVisible: null,
    };
  }
}

// Get user statistics
export async function getUserStats(): Promise<UserStats> {
  try {
    // Get total users
    const usersQuery = query(collection(db, 'users'));
    const usersSnapshot = await getDocs(usersQuery);
    const totalUsers = usersSnapshot.size;

    // Get premium users
    const premiumUsersQuery = query(collection(db, 'users'), where('isPremium', '==', true));
    const premiumUsersSnapshot = await getDocs(premiumUsersQuery);
    const premiumUsers = premiumUsersSnapshot.size;

    // Get active users (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const activeUsersQuery = query(collection(db, 'users'), where('lastLogin', '>=', sevenDaysAgo));
    const activeUsersSnapshot = await getDocs(activeUsersQuery);
    const activeUsers = activeUsersSnapshot.size;

    // Get new users (created in last 7 days)
    const newUsersQuery = query(collection(db, 'users'), where('createdAt', '>=', sevenDaysAgo));
    const newUsersSnapshot = await getDocs(newUsersQuery);
    const newUsers = newUsersSnapshot.size;

    return {
      totalUsers,
      activeUsers,
      premiumUsers,
      newUsers,
    };
  } catch (error) {
    // Return default values instead of throwing
    return {
      totalUsers: 0,
      activeUsers: 0,
      premiumUsers: 0,
      newUsers: 0,
    };
  }
}

// Get element reports with pagination
export async function getElementReports(lastReport = null, pageSize = 20) {
  try {
    let reportsQuery: any;

    // Simplified query without composite index requirements
    if (lastReport) {
      reportsQuery = query(
        collection(db, 'help_reports'),
        orderBy('timestamp', 'desc'),
        startAfter(lastReport),
        limit(pageSize)
      );
    } else {
      reportsQuery = query(
        collection(db, 'help_reports'),
        orderBy('timestamp', 'desc'),
        limit(pageSize)
      );
    }

    const reportsSnapshot = await getDocs(reportsQuery);
    // Filter for element reports client-side
    const reports = reportsSnapshot.docs
      .filter((doc) => (doc.data() as any).type === 'element')
      .map((doc) => ({
        id: doc.id,
        ...(doc.data() as any),
      }));

    return {
      reports,
      lastVisible: reportsSnapshot.docs[reportsSnapshot.docs.length - 1],
    };
  } catch (error) {
    // Return empty array instead of throwing
    return {
      reports: [],
      lastVisible: null,
    };
  }
}

// Get issue reports with pagination
export async function getIssueReports(lastReport = null, pageSize = 20) {
  try {
    let reportsQuery: any;

    // Simplified query without composite index requirements
    if (lastReport) {
      reportsQuery = query(
        collection(db, 'help_reports'),
        orderBy('timestamp', 'desc'),
        startAfter(lastReport),
        limit(pageSize)
      );
    } else {
      reportsQuery = query(
        collection(db, 'help_reports'),
        orderBy('timestamp', 'desc'),
        limit(pageSize)
      );
    }

    const reportsSnapshot = await getDocs(reportsQuery);
    // Filter for issue reports client-side
    const reports = reportsSnapshot.docs
      .filter((doc) => (doc.data() as any).type === 'issue')
      .map((doc) => ({
        id: doc.id,
        ...(doc.data() as any),
      }));

    return {
      reports,
      lastVisible: reportsSnapshot.docs[reportsSnapshot.docs.length - 1],
    };
  } catch (error) {
    // Return empty array instead of throwing
    return {
      reports: [],
      lastVisible: null,
    };
  }
}

// Update report status
export async function updateReportStatus(
  reportId: string,
  status: string,
  noteText: string,
  adminUid: string,
  adminName?: string
) {
  try {
    const reportRef = doc(db, 'help_reports', reportId);

    // First get the current report to check if notes is already an array
    const reportSnap = await getDoc(reportRef);
    if (!reportSnap.exists()) {
      throw new Error('Report not found');
    }

    const reportData = reportSnap.data();
    let updatedNotes = reportData.notes;

    // Only add a note if there's actual text content
    if (noteText && noteText.trim() !== '') {
      // Create a new note object
      const newNote = {
        text: noteText,
        timestamp: new Date(), // Use JavaScript Date instead of serverTimestamp
        adminId: adminUid,
        adminName: adminName || 'Admin',
        type: 'note', // Always 'note' type - we don't track status changes anymore
      };

      // Check if notes already exists and what type it is
      if (!reportData.notes) {
        // No notes yet, create a new array with this note
        updatedNotes = [newNote];
      } else if (typeof reportData.notes === 'string') {
        // Convert from string to array, preserving the old note as legacy
        updatedNotes = [
          {
            text: reportData.notes,
            timestamp: reportData.reviewedAt || reportData.timestamp,
            adminId: reportData.reviewedBy || 'unknown',
            adminName: 'Admin',
            type: 'note',
          },
          newNote,
        ];
      } else if (Array.isArray(reportData.notes)) {
        // Already an array, add the new note
        updatedNotes = [...reportData.notes, newNote];
      } else {
        // Unexpected type, create new array
        updatedNotes = [newNote];
      }
    }

    // Update the document
    const updateData: any = {
      status,
      reviewedBy: adminUid,
      reviewedAt: new Date(), // Use JavaScript Date instead of serverTimestamp
    };

    // Only include notes in the update if they've changed
    if (updatedNotes !== reportData.notes) {
      updateData.notes = updatedNotes;
    }

    await updateDoc(reportRef, updateData);

    return { success: true };
  } catch (error) {
    // Return failure instead of throwing
    return { success: false, error: 'Failed to update report status' };
  }
}

// API usage tracking has been removed

// Get word usage statistics
export async function getWordUsageStats(forgeType?: string, limitCount = 20, wordType?: string) {
  try {
    // Create a base query
    let wordUsageQuery = query(collection(db, 'word_usage'), limit(500)); // Get more data for client-side filtering

    try {
      const wordUsageSnapshot = await getDocs(wordUsageQuery);
      let wordUsage = wordUsageSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as any),
      })) as WordUsage[];

      // Apply filters client-side
      if (forgeType) {
        wordUsage = wordUsage.filter((word) => word.forgeType === forgeType);
      }

      if (wordType) {
        wordUsage = wordUsage.filter((word) => word.type === wordType);
      }

      // Sort by count descending
      wordUsage.sort((a, b) => (b.count || 0) - (a.count || 0));

      // Limit to requested count
      wordUsage = wordUsage.slice(0, limitCount);

      return wordUsage;
    } catch (queryError) {
      // Return sample data instead of empty array
      return generateSampleWordUsage(forgeType, limitCount, wordType);
    }
  } catch (error) {
    // Return sample data instead of empty array
    return generateSampleWordUsage(forgeType, limitCount, wordType);
  }
}

// Helper function to generate sample word usage data
function generateSampleWordUsage(
  forgeType?: string,
  limitCount = 20,
  wordType?: string
): WordUsage[] {
  const sampleWords = [
    { word: 'water', type: 'input', forgeType: 'Item', count: 50 },
    { word: 'fire', type: 'input', forgeType: 'Item', count: 45 },
    { word: 'earth', type: 'input', forgeType: 'Item', count: 40 },
    { word: 'air', type: 'input', forgeType: 'Item', count: 35 },
    { word: 'steam', type: 'output', forgeType: 'Item', count: 30 },
    { word: 'mud', type: 'output', forgeType: 'Item', count: 25 },
    { word: 'dust', type: 'output', forgeType: 'Item', count: 20 },
    { word: 'energy', type: 'output', forgeType: 'Item', count: 15 },
    { word: 'mountain', type: 'input', forgeType: 'Location', count: 10 },
    { word: 'forest', type: 'output', forgeType: 'Location', count: 5 },
  ];

  // Filter by forge type if specified
  let filteredWords = sampleWords;
  if (forgeType) {
    filteredWords = filteredWords.filter((word) => word.forgeType === forgeType);
  }

  // Filter by word type if specified
  if (wordType) {
    filteredWords = filteredWords.filter((word) => word.type === wordType);
  }

  // Return limited number of words
  return filteredWords.slice(0, limitCount).map((word, index) => ({
    id: `sample_${index}`,
    word: word.word,
    type: word.type as 'input' | 'output' | 'goal', // Type assertion to match WordUsage type
    forgeType: word.forgeType as 'Item' | 'Location' | 'Character' | 'Goal' | 'Deconstructor', // Type assertion to match WordUsage type
    count: word.count,
    inputCount: word.type === 'input' ? word.count : 0,
    outputCount: word.type === 'output' ? word.count : 0,
    timestamp: new Date(),
    users: ['sample_user'],
  }));
}

// Get word relationships for concept map
export async function getWordRelationships() {
  try {
    // Get all word usage data
    const wordUsageQuery = query(collection(db, 'word_usage'), limit(500));

    let wordUsage: WordUsage[] = [];

    try {
      const wordUsageSnapshot = await getDocs(wordUsageQuery);
      wordUsage = wordUsageSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as any),
      })) as WordUsage[];
    } catch (queryError) {
      // Use sample data instead
      wordUsage = generateSampleWordUsage(undefined, 500);
    }

    // Create nodes and edges for the concept map
    const nodes: any[] = [];
    const edges: any[] = [];
    const nodeMap = new Map();

    // First, create a map to track all unique words (regardless of input/output type)
    const uniqueWordsMap = new Map();

    // Group words by their actual word (not by type)
    wordUsage.forEach((word) => {
      const wordKey = word.word.toLowerCase();

      if (!uniqueWordsMap.has(wordKey)) {
        uniqueWordsMap.set(wordKey, {
          word: word.word,
          count: word.count || 0,
          inputCount: word.inputCount || 0,
          outputCount: word.outputCount || 0,
          forgeTypes: new Set([word.forgeType]),
          connectionCount: 0, // Will be calculated later
        });
      } else {
        // Update existing word entry
        const existingWord = uniqueWordsMap.get(wordKey);
        existingWord.count += word.count || 0;
        existingWord.inputCount += word.inputCount || 0;
        existingWord.outputCount += word.outputCount || 0;
        existingWord.forgeTypes.add(word.forgeType);
      }
    });

    // Create nodes for all unique words
    uniqueWordsMap.forEach((wordData, wordKey) => {
      // Create a unique ID for the node - just use the word itself
      const nodeId = wordKey;

      // Create the node
      const node = {
        id: nodeId,
        label: wordData.word,
        count: wordData.count,
        inputCount: wordData.inputCount,
        outputCount: wordData.outputCount,
        forgeTypes: Array.from(wordData.forgeTypes),
        connectionCount: 0, // Will be updated after we process relationships
      };

      // Add the node to the map and array
      nodeMap.set(nodeId, node);
      nodes.push(node);
    });

    // Create a map to track direct input-output relationships
    const relationshipMap = new Map();

    // Process each word to extract its direct relationships
    wordUsage.forEach((word) => {
      // If this is an input word with output connections
      if (word.type === 'input' && word.inputConnections) {
        const sourceWordKey = word.word.toLowerCase();

        // For each output this input is connected to
        Object.entries(word.inputConnections).forEach(([outputWord, count]) => {
          const targetWordKey = outputWord.toLowerCase();

          // Create a unique key for this relationship
          const relationshipKey = `${sourceWordKey}|${targetWordKey}`;

          // Add the relationship with the correct count
          if (!relationshipMap.has(relationshipKey)) {
            relationshipMap.set(relationshipKey, {
              source: sourceWordKey,
              target: targetWordKey,
              value: count,
              forgeType: word.forgeType,
            });

            // Increment connection count for both nodes
            if (nodeMap.has(sourceWordKey)) {
              nodeMap.get(sourceWordKey).connectionCount += 1;
            }
            if (nodeMap.has(targetWordKey)) {
              nodeMap.get(targetWordKey).connectionCount += 1;
            }
          } else {
            // Increment the relationship strength
            relationshipMap.get(relationshipKey).value += count;
          }
        });
      }

      // If this is an output word with input connections
      if (word.outputConnections) {
        const targetWordKey = word.word.toLowerCase();

        // For each input this output is connected to
        Object.entries(word.outputConnections).forEach(([inputWord, count]) => {
          const sourceWordKey = inputWord.toLowerCase();

          // Create a unique key for this relationship
          const relationshipKey = `${sourceWordKey}|${targetWordKey}`;

          // Add the relationship with the correct count
          if (!relationshipMap.has(relationshipKey)) {
            relationshipMap.set(relationshipKey, {
              source: sourceWordKey,
              target: targetWordKey,
              value: count,
              forgeType: word.forgeType,
            });

            // Increment connection count for both nodes
            if (nodeMap.has(sourceWordKey)) {
              nodeMap.get(sourceWordKey).connectionCount += 1;
            }
            if (nodeMap.has(targetWordKey)) {
              nodeMap.get(targetWordKey).connectionCount += 1;
            }
          } else {
            // Increment the relationship strength
            relationshipMap.get(relationshipKey).value += count;
          }
        });
      }

      // If this word has deconstructor connections
      if (word.deconstructorConnections) {
        const targetWordKey = word.word.toLowerCase();

        // For each input this output is connected to via deconstructor
        Object.entries(word.deconstructorConnections).forEach(([inputWord, count]) => {
          const sourceWordKey = inputWord.toLowerCase();

          // Create a unique key for this relationship
          const relationshipKey = `${sourceWordKey}|${targetWordKey}`;

          // Add the relationship with the correct count
          if (!relationshipMap.has(relationshipKey)) {
            relationshipMap.set(relationshipKey, {
              source: sourceWordKey,
              target: targetWordKey,
              value: count,
              forgeType: 'Deconstructor',
            });

            // Increment connection count for both nodes
            if (nodeMap.has(sourceWordKey)) {
              nodeMap.get(sourceWordKey).connectionCount += 1;
            }
            if (nodeMap.has(targetWordKey)) {
              nodeMap.get(targetWordKey).connectionCount += 1;
            }
          } else {
            // Increment the relationship strength
            relationshipMap.get(relationshipKey).value += count;
          }
        });
      }
    });

    // If we don't have any relationships from the new structure,
    // fall back to the old method of connecting by forge type
    if (relationshipMap.size === 0) {
      // We don't need to extract these separately anymore, as we're using the wordsByForgeType map

      // Group words by forge type for easier processing
      const wordsByForgeType = new Map();

      wordUsage.forEach((word) => {
        if (!wordsByForgeType.has(word.forgeType)) {
          wordsByForgeType.set(word.forgeType, {
            inputs: [],
            outputs: [],
          });
        }

        const forgeWords = wordsByForgeType.get(word.forgeType);
        if (word.type === 'input') {
          forgeWords.inputs.push(word);
        } else if (word.type === 'output') {
          forgeWords.outputs.push(word);
        }
      });

      // For each forge type, create direct input-output relationships
      wordsByForgeType.forEach((forgeWords, _forgeType) => {
        // Prefixed with underscore to indicate it's not used
        // For each output in this forge type
        forgeWords.outputs.forEach((output: any) => {
          const targetWordKey = output.word.toLowerCase();

          // Connect to inputs of the same forge type
          forgeWords.inputs.forEach((input: any) => {
            const sourceWordKey = input.word.toLowerCase();

            // Create a unique key for this relationship
            const relationshipKey = `${sourceWordKey}|${targetWordKey}`;

            // Only add if we don't already have this relationship
            if (!relationshipMap.has(relationshipKey)) {
              relationshipMap.set(relationshipKey, {
                source: sourceWordKey,
                target: targetWordKey,
                value: 1,
                forgeType: input.forgeType,
              });

              // Increment connection count for both nodes
              if (nodeMap.has(sourceWordKey)) {
                nodeMap.get(sourceWordKey).connectionCount += 1;
              }
              if (nodeMap.has(targetWordKey)) {
                nodeMap.get(targetWordKey).connectionCount += 1;
              }
            } else {
              // Increment the relationship strength
              relationshipMap.get(relationshipKey).value += 1;
            }
          });
        });
      });
    }

    // Update node colors based on connection count
    nodes.forEach((node) => {
      // Calculate a heat value based on connection count
      // More connections = "hotter" (more red)
      const maxConnections = Math.max(...nodes.map((n) => n.connectionCount));
      const connectionRatio = maxConnections > 0 ? node.connectionCount / maxConnections : 0;

      // Generate a color from blue (few connections) to red (many connections)
      // Using a simple linear interpolation between blue and red
      const r = Math.floor(50 + connectionRatio * 205); // 50-255
      const g = Math.floor(50 + (1 - connectionRatio) * 100); // 50-150
      const b = Math.floor(50 + (1 - connectionRatio) * 205); // 50-255

      node.color = `rgb(${r}, ${g}, ${b})`;
    });

    // Convert relationships to edges
    relationshipMap.forEach((relationship) => {
      edges.push(relationship);
    });

    return { nodes, edges };
  } catch (error) {
    // Generate sample data for the concept map
    const sampleNodes = [
      { id: 'water', label: 'Water', count: 50, connectionCount: 2, color: 'rgb(50, 50, 255)' },
      { id: 'fire', label: 'Fire', count: 45, connectionCount: 1, color: 'rgb(100, 50, 200)' },
      { id: 'earth', label: 'Earth', count: 40, connectionCount: 1, color: 'rgb(100, 50, 200)' },
      { id: 'steam', label: 'Steam', count: 30, connectionCount: 2, color: 'rgb(150, 50, 150)' },
      { id: 'mud', label: 'Mud', count: 25, connectionCount: 2, color: 'rgb(150, 50, 150)' },
    ];

    const sampleEdges = [
      { source: 'water', target: 'steam', value: 1, forgeType: 'Item' },
      { source: 'fire', target: 'steam', value: 1, forgeType: 'Item' },
      { source: 'water', target: 'mud', value: 1, forgeType: 'Item' },
      { source: 'earth', target: 'mud', value: 1, forgeType: 'Item' },
    ];

    return { nodes: sampleNodes, edges: sampleEdges };
  }
}

// Get dashboard statistics
export async function getDashboardStats(): Promise<DashboardStats> {
  try {
    // Default stats in case of errors
    const defaultStats: DashboardStats = {
      userStats: {
        totalUsers: 0,
        activeUsers: 0,
        premiumUsers: 0,
        newUsers: 0,
      },
      totalElements: 0,
      totalReports: 0,
      mostUsedWords: [],
    };

    // Get user stats with error handling
    let userStats: UserStats;
    try {
      userStats = await getUserStats();
    } catch (error) {
      userStats = defaultStats.userStats;
    }

    // Get total elements with error handling
    let totalElements = 0;
    try {
      const elementsQuery = query(collection(db, 'elements'));
      const elementsSnapshot = await getDocs(elementsQuery);
      totalElements = elementsSnapshot.size;
    } catch (error) {}

    // Get total reports with error handling (excluding dismissed reports)
    let totalReports = 0;
    try {
      // Query for reports that are not dismissed
      const reportsQuery = query(
        collection(db, 'help_reports'),
        where('status', '!=', 'dismissed')
      );
      const reportsSnapshot = await getDocs(reportsQuery);
      totalReports = reportsSnapshot.size;
    } catch (error) {
      // Fallback to counting all reports and filtering client-side if the query fails
      try {
        const allReportsQuery = query(collection(db, 'help_reports'));
        const allReportsSnapshot = await getDocs(allReportsQuery);

        // Filter out dismissed reports
        totalReports = allReportsSnapshot.docs.filter(
          (doc) => doc.data().status !== 'dismissed'
        ).length;
      } catch (fallbackError) {}
    }

    // API usage tracking has been removed

    // Get most used words with error handling
    let mostUsedWords = [];
    try {
      // Import the new word usage API
      const { getTopWordUsage } = await import('./wordUsageApi');

      // Get top 50 words
      const allWords = await getTopWordUsage(50);

      // Process to get top 10 overall
      const topOverall = [...allWords].sort((a, b) => b.totalCount - a.totalCount).slice(0, 10);

      // Get top 10 for each forge type
      const itemWords = allWords
        .filter((word) => word.forgeTypes.includes('Item'))
        .sort((a, b) => b.totalCount - a.totalCount)
        .slice(0, 10);

      const locationWords = allWords
        .filter((word) => word.forgeTypes.includes('Location'))
        .sort((a, b) => b.totalCount - a.totalCount)
        .slice(0, 10);

      const characterWords = allWords
        .filter((word) => word.forgeTypes.includes('Character'))
        .sort((a, b) => b.totalCount - a.totalCount)
        .slice(0, 10);

      // Combine all top words
      mostUsedWords = [...topOverall, ...itemWords, ...locationWords, ...characterWords];
    } catch (error) {}

    return {
      userStats,
      totalElements,
      totalReports,
      mostUsedWords,
    };
  } catch (error) {
    // Return default stats instead of throwing
    return {
      userStats: {
        totalUsers: 0,
        activeUsers: 0,
        premiumUsers: 0,
        newUsers: 0,
      },
      totalElements: 0,
      totalReports: 0,
      mostUsedWords: [],
    };
  }
}

// Completely delete a word from all collections
export async function deleteWordCompletely(wordToDelete: string): Promise<void> {
  try {
    // Check if user is authenticated and is admin
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error('You must be logged in to perform this action');
    }

    // Check if the user is an admin
    const userRef = doc(db, 'users', currentUser.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }

    const userData = userDoc.data();
    const isAdmin = userData.isAdmin === true || currentUser.uid === 'tOCaPaZ9XDb3eBXgoNYzGPWeyY2';

    if (!isAdmin) {
      throw new Error('You must be an admin to perform this action');
    }

    const normalizedWord = wordToDelete.toLowerCase().trim();
    console.log(`Starting complete deletion of word: "${normalizedWord}"`);

    // 1. Delete from word_usage collection
    console.log('Deleting from word_usage collection...');
    const wordUsageRef = doc(db, 'word_usage', normalizedWord);
    try {
      await deleteDoc(wordUsageRef);
    } catch (error) {
      // Word might not exist, continue with cleanup
      console.log('Word not found in word_usage collection, continuing with cleanup...');
    }

    // 2. Remove references from other word_usage documents
    console.log('Removing references from other word_usage documents...');
    const allWordUsageQuery = query(collection(db, 'word_usage'));
    const allWordUsageSnapshot = await getDocs(allWordUsageQuery);

    // Process in smaller batches to avoid hitting Firestore limits
    let batch = writeBatch(db);
    let batchCount = 0;
    const MAX_BATCH_SIZE = 400; // Conservative limit

    for (const wordDoc of allWordUsageSnapshot.docs) {
      const wordData = wordDoc.data();
      let needsUpdate = false;
      const updateData: any = {};

      // Check inputToOutputs
      if (wordData.inputToOutputs && wordData.inputToOutputs[normalizedWord]) {
        updateData[`inputToOutputs.${normalizedWord}`] = deleteField(); // Delete the field
        needsUpdate = true;
      }

      // Check outputFromInputs
      if (wordData.outputFromInputs && wordData.outputFromInputs[normalizedWord]) {
        updateData[`outputFromInputs.${normalizedWord}`] = deleteField(); // Delete the field
        needsUpdate = true;
      }

      if (needsUpdate) {
        batch.update(wordDoc.ref, updateData);
        batchCount++;

        // Commit batch if it's getting full
        if (batchCount >= MAX_BATCH_SIZE) {
          await batch.commit();
          // Create new batch for remaining operations
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
    }

    // Commit any remaining operations
    if (batchCount > 0) {
      await batch.commit();
    }

    // 3. Remove from all user game states
    console.log('Removing from user game states...');
    const usersQuery = query(collection(db, 'users'));
    const usersSnapshot = await getDocs(usersQuery);

    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const gameState = userData.gameState;

      if (!gameState) continue;

      let needsUpdate = false;
      const updateData: any = {};

      // Remove from elements array
      if (gameState.elements && Array.isArray(gameState.elements)) {
        const filteredElements = gameState.elements.filter(
          (element: any) => element.name?.toLowerCase() !== normalizedWord
        );
        if (filteredElements.length !== gameState.elements.length) {
          updateData['gameState.elements'] = filteredElements;
          needsUpdate = true;
        }
      }

      // Remove from discoveries array
      if (gameState.discoveries && Array.isArray(gameState.discoveries)) {
        const filteredDiscoveries = gameState.discoveries.filter(
          (discovery: any) => discovery.name?.toLowerCase() !== normalizedWord
        );
        if (filteredDiscoveries.length !== gameState.discoveries.length) {
          updateData['gameState.discoveries'] = filteredDiscoveries;
          needsUpdate = true;
        }
      }

      // Remove from wordHistory
      if (gameState.wordHistory) {
        ['Item', 'Location', 'Character'].forEach((forgeType) => {
          if (gameState.wordHistory[forgeType] && Array.isArray(gameState.wordHistory[forgeType])) {
            const filteredHistory = gameState.wordHistory[forgeType].filter(
              (word: string) => word.toLowerCase() !== normalizedWord
            );
            if (filteredHistory.length !== gameState.wordHistory[forgeType].length) {
              updateData[`gameState.wordHistory.${forgeType}`] = filteredHistory;
              needsUpdate = true;
            }
          }
        });
      }

      // Remove from combinations (keys that contain the word)
      if (gameState.combinations) {
        const filteredCombinations: any = {};
        let combinationsChanged = false;

        Object.entries(gameState.combinations).forEach(([key, value]) => {
          const combinationWords = key.toLowerCase().split('+');
          const resultWord = (value as string).toLowerCase();

          // Keep combination only if it doesn't involve the deleted word
          if (!combinationWords.includes(normalizedWord) && resultWord !== normalizedWord) {
            filteredCombinations[key] = value;
          } else {
            combinationsChanged = true;
          }
        });

        if (combinationsChanged) {
          updateData['gameState.combinations'] = filteredCombinations;
          needsUpdate = true;
        }
      }

      // Remove from deconstructions
      if (gameState.deconstructions) {
        const filteredDeconstructions: any = {};
        let deconstructionsChanged = false;

        Object.entries(gameState.deconstructions).forEach(([key, value]) => {
          if (key.toLowerCase() !== normalizedWord) {
            filteredDeconstructions[key] = value;
          } else {
            deconstructionsChanged = true;
          }
        });

        if (deconstructionsChanged) {
          updateData['gameState.deconstructions'] = filteredDeconstructions;
          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        const newBatch = writeBatch(db);
        newBatch.update(userDoc.ref, updateData);
        await newBatch.commit();
      }
    }

    console.log(`Successfully deleted word "${normalizedWord}" from all collections`);
  } catch (error) {
    console.error('Error deleting word completely:', error);
    throw new Error(`Failed to delete word: ${error.message}`);
  }
}

// Clean up all null values from word_usage collection
export async function cleanupNullReferences(): Promise<void> {
  try {
    // Check if user is authenticated and is admin
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error('You must be logged in to perform this action');
    }

    // Check if the user is an admin
    const userRef = doc(db, 'users', currentUser.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }

    const userData = userDoc.data();
    const isAdmin = userData.isAdmin === true || currentUser.uid === 'tOCaPaZ9XDb3eBXgoNYzGPWeyY2';

    if (!isAdmin) {
      throw new Error('You must be an admin to perform this action');
    }

    console.log('Starting cleanup of null references in word_usage collection...');

    // Get all word_usage documents
    const allWordUsageQuery = query(collection(db, 'word_usage'));
    const allWordUsageSnapshot = await getDocs(allWordUsageQuery);

    let batch = writeBatch(db);
    let batchCount = 0;
    const MAX_BATCH_SIZE = 400;
    let totalCleaned = 0;

    for (const wordDoc of allWordUsageSnapshot.docs) {
      const wordData = wordDoc.data();
      let needsUpdate = false;
      const updateData: any = {};

      // Check inputToOutputs for null values
      if (wordData.inputToOutputs && typeof wordData.inputToOutputs === 'object') {
        Object.entries(wordData.inputToOutputs).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            updateData[`inputToOutputs.${key}`] = deleteField();
            needsUpdate = true;
            totalCleaned++;
          }
        });
      }

      // Check outputFromInputs for null values
      if (wordData.outputFromInputs && typeof wordData.outputFromInputs === 'object') {
        Object.entries(wordData.outputFromInputs).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            updateData[`outputFromInputs.${key}`] = deleteField();
            needsUpdate = true;
            totalCleaned++;
          }
        });
      }

      if (needsUpdate) {
        batch.update(wordDoc.ref, updateData);
        batchCount++;

        // Commit batch if it's getting full
        if (batchCount >= MAX_BATCH_SIZE) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
    }

    // Commit any remaining operations
    if (batchCount > 0) {
      await batch.commit();
    }

    console.log(
      `Successfully cleaned up ${totalCleaned} null references from word_usage collection`
    );
  } catch (error) {
    console.error('Error cleaning up null references:', error);
    throw new Error(`Failed to cleanup null references: ${error.message}`);
  }
}
